import { computed, effect, Signal } from '@angular/core';
import { Logger } from '@core/logger';
import { select } from '@ngxs/store';
import { MatterDocumentPackagesAggregatorState } from './matter-document-packages-aggregator.state';
import { TrustLedgerSelectors } from '@state/global/trust-ledger/trust_ledger';

export interface TrustLedgerPackageWarning {
	shouldShowWarning: boolean;
	containsTrustLedger: boolean;
	isBalanced: boolean;
	difference: number;
	trustLedgerDocumentCount: number;
}

/**
 * Creates a computed signal to check if a package should show Trust Ledger unbalanced warning.
 *
 * WARNING: To avoid circular deps between TrustLedger and Package states,
 * this utility must be used in components, NOT in selectors.
 *
 * @param packageId - Signal containing the package ID to check
 * @returns Computed signal with warning information
 */
export function createTrustLedgerPackageWarning(
	packageId: Signal<string>,
): Signal<TrustLedgerPackageWarning> {
	const tlDocuments = select(
		MatterDocumentPackagesAggregatorState.getTrustLedgerDocumentsForPackage,
	);

	const trustLedger = select(TrustLedgerSelectors.getTrustLedger);

	return computed(() => {
		const pkgId = packageId();
		if (!pkgId) {
			return {
				shouldShowWarning: false,
				containsTrustLedger: false,
				isBalanced: true,
				difference: 0,
				trustLedgerDocumentCount: 0,
			};
		}

		const tlDocs = tlDocuments()(pkgId);
		const containsTL = tlDocs.length > 0;
		const tl = trustLedger();

		// TL is balanced if difference === 0
		const difference = tl?.totals?.difference ?? 0;
		const isBalanced = difference === 0;

		return {
			shouldShowWarning: containsTL && !isBalanced,
			containsTrustLedger: containsTL,
			isBalanced,
			difference,
			trustLedgerDocumentCount: tlDocs.length,
		};
	});
}

/**
 * Logs Trust Ledger balance information when a package contains trust ledger documents.
 * Must be called within an injection context (e.g., component constructor).
 *
 * @param warning - Signal containing Trust Ledger warning information
 */
export function logTrustLedgerWarning(
	warning: Signal<TrustLedgerPackageWarning>,
): void {
	effect(() => {
		const w = warning();
		if (w.containsTrustLedger) {
			Logger.info(
				`Found a ${
					w.isBalanced ? 'balanced' : `unbalanced by ${w.difference}`
				} Trust Ledger within package`,
			);
		}
	});
}
